import { useCallback, useEffect, useState, useMemo } from "react";
import { countTokens } from "../utils";

export const useInputHandling = (
  message,
  setMessage,
  getCaretCoordinates,
  handleMentionDetection,
  setMentionTriggerPos,
  setMentionDropdownOpen,
  toast,
  threadMessages = []
) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputTokens, setInputTokens] = useState(0);

  // Calculate total tokens from all thread messages
  const threadTokens = useMemo(() => {
    if (!threadMessages || threadMessages.length === 0) return 0;

    return threadMessages.reduce((total, msg) => {
      // Count tokens from message content
      let tokens = 0;

      // Handle different content formats
      if (typeof msg.content === 'string') {
        tokens = countTokens(msg.content);
      } else if (Array.isArray(msg.content)) {
        // Handle array content (e.g., text and image parts)
        tokens = msg.content.reduce((sum, part) => {
          if (part.type === 'text' && part.text) {
            return sum + countTokens(part.text);
          }
          // For images, we could add image token estimation here if needed
          return sum;
        }, 0);
      }

      return total + tokens;
    }, 0);
  }, [threadMessages]);

  // Calculate total tokens: thread messages + current input
  const calculateTotalTokens = useCallback((currentMessage) => {
    const currentInputTokens = countTokens(currentMessage || '');
    return threadTokens + currentInputTokens;
  }, [threadTokens]);

  // Handle input changes including mention detection
  const handleInputChange = useCallback(
    (e) => {
      setIsFocused(true);
      const newMessage = e.target.value;
      const totalTokenCount = calculateTotalTokens(newMessage);
      setInputTokens(totalTokenCount);
      setMessage(newMessage);

      // Check for @ mentions - improved detection
      const caretPos = e.target.selectionStart;
      const mentionDetected = handleMentionDetection(newMessage, caretPos);

      if (mentionDetected) {
        // Position the dropdown at caret
        const coords = getCaretCoordinates();

        if (coords) {
          setMentionTriggerPos(coords);
          setMentionDropdownOpen(true);
        }
      }
    },
    [
      getCaretCoordinates,
      handleMentionDetection,
      setMentionTriggerPos,
      setMentionDropdownOpen,
      setMessage,
      calculateTotalTokens,
    ]
  );

  // Update token count when message or thread messages change
  useEffect(() => {
    const totalTokenCount = calculateTotalTokens(message);
    setInputTokens(totalTokenCount);
  }, [message, calculateTotalTokens]);

  return {
    isFocused,
    setIsFocused,
    inputTokens,
    setInputTokens,
    handleInputChange,
  };
};
